import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Add, Create } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { useGetQuoteQuery } from '@/app/services/quotes'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useUpdateQuoteItem } from '@/common/hooks/useUpdateQuoteItem'

import CustomPropertySecProcess from './CustomPropertySecProcess'
import QuantitySecProcess from './QuantitySecProcess'
import SecondaryProcessesModal from './SecondaryProcessesModal'

const classes = {
    secProcessesSection: {
        width: '100%',
        gap: 2,
    },
    bold: {
        fontWeight: 'bold',
    },
    formControl_small: {
        width: '80px',
    },
    itemRow: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
}

const ItemSecondaryProcesses = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

    const [itemSecondaryProcesses, setItemSecondaryProcesses] = useState(quoteItem?.secondaryProcesses)
    const [prevSecondaryProcesses, setPrevSecondaryProcesses] = useState(quoteItem?.secondaryProcesses)

    if (quoteItem?.secondaryProcesses !== prevSecondaryProcesses) {
        setPrevSecondaryProcesses(quoteItem?.secondaryProcesses)
        setItemSecondaryProcesses(quoteItem?.secondaryProcesses)
    }

    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({
        organisationId,
        requestParams: { includeDeleted: false },
    })

    const { isFetching: isFetchingQuote } = useGetQuoteQuery({
        organisationId,
        quoteId,
    })

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const { handleUpdateQuoteItem, markItemPending } = useUpdateQuoteItem()

    const handleEditButtonClick = (event) => {
        event.stopPropagation()
        setIsEditDialogOpen(true)
    }

    const onCloseEditDialog = async (processes) => {
        setIsEditDialogOpen(false)

        if (processes) {
            setItemSecondaryProcesses(processes)
            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({ ...quoteItem, secondaryProcesses: processes })
        }
    }

    const debouncedSave = useDebouncedCallback(async () => {
        await handleUpdateQuoteItem({ ...quoteItem, secondaryProcesses: itemSecondaryProcesses })
    }, 200)

    const onInputValueChange = (process) => {
        markItemPending(quoteItemId)

        const processIds = itemSecondaryProcesses?.map((sp) => sp.secondaryProcessId)
        const processIndex = processIds.indexOf(process.secondaryProcessId)
        const newProcessList = [...itemSecondaryProcesses]
        newProcessList[processIndex] = process
        setItemSecondaryProcesses(newProcessList)

        debouncedSave()
    }

    const getProcessDetails = useCallback(
        (processId) => organisationSecondaryProcesses?.find((osp) => osp.secondaryProcessId === processId),
        [organisationSecondaryProcesses]
    )

    const sortedProcesses = useMemo(() => {
        return itemSecondaryProcesses
            ?.map((process) => ({
                ...process,
                index: getProcessDetails(process.secondaryProcessId)?.index,
                customPropertyName: getProcessDetails(process.secondaryProcessId)?.customPropertyName,
                customPropertyOptions: getProcessDetails(process.secondaryProcessId)?.customPropertyOptions,
                customValue: process.customValue,
            }))
            .sort((a, b) => (a.index < b.index ? -1 : 1))
    }, [itemSecondaryProcesses, getProcessDetails])

    return (
        <Box
            sx={[
                {
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: '1',
                    flexDirection: 'column',
                },
                classes.secProcessesSection,
            ]}
        >
            <Box sx={classes.itemRow}>
                <Typography
                    data-testid="nqp-secondary-processes-section-title"
                    sx={classes.bold}
                >
                    {t('Secondary processes')}
                </Typography>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <span>
                        <IconButton
                            aria-label="edit"
                            color="primary"
                            data-testid="nqp-secondary-processes-add-button"
                            disabled={isFetchingQuote || isPartFromLibrary}
                            size="small"
                            onClick={(event) => handleEditButtonClick(event)}
                            onFocus={(event) => event.stopPropagation()}
                        >
                            {itemSecondaryProcesses?.length > 0 ? <Create /> : <Add />}
                        </IconButton>
                    </span>
                </TbxTooltip>
            </Box>
            {sortedProcesses?.map((process) => (
                <Box
                    key={process.secondaryProcessId}
                    sx={classes.itemRow}
                >
                    <Typography
                        data-testid="nqp-quote-item-secondary-process-label"
                        variant="body2"
                    >
                        {getProcessDetails(process.secondaryProcessId)?.name}
                    </Typography>
                    <Box
                        sx={{
                            gap: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        {getProcessDetails(process.secondaryProcessId)?.customPropertyName ? (
                            <CustomPropertySecProcess
                                isPartFromLibrary={isPartFromLibrary}
                                process={process}
                                quoteItemId={quoteItemId}
                                onInputChange={onInputValueChange}
                            />
                        ) : null}
                        {getProcessDetails(process.secondaryProcessId)?.measureType === 'Quantity' ? (
                            <QuantitySecProcess
                                isPartFromLibrary={isPartFromLibrary}
                                process={process}
                                onInputChange={onInputValueChange}
                            />
                        ) : null}
                    </Box>
                </Box>
            ))}
            {isEditDialogOpen ? (
                <SecondaryProcessesModal
                    open={isEditDialogOpen}
                    secondaryProcesses={quoteItem?.secondaryProcesses}
                    onClose={onCloseEditDialog}
                />
            ) : null}
        </Box>
    )
}

ItemSecondaryProcesses.propTypes = {
    quoteItemId: PropTypes.string,
}

export default memo(ItemSecondaryProcesses)
